import React, { useState } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import PythonIcon from "@/assets/images/python.svg?react";
import type { PersonalAccessToken } from "target_wasm";
import { Button } from "components/common/Button";
import { CodeBlock } from "components/common/CodeBlock";
import { Fold } from "components/common/Fold";
import { SearchBar } from "components/common/SearchBar";
import { SectionRule } from "components/common/SectionRule";
import { MetaAddButton } from "components/main/datasets/DatasetMetaSection";
import { HintRow } from "components/main/overview/ListEmptyShell";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { deleteToken, revokeToken } from "./TokensApi";
import { MAX_VISIBLE_TOKENS } from "./utils";
import { ROW_GRID, TokenRow } from "./TokenRow";

const PYTHON_QUICKSTART = `pip install pymycorr
export MYCORR_API_TOKEN=<your token>`;
const HEADERS = ["Name", "Scope", "Created", "Expires"];
const newestFirst = (a: PersonalAccessToken, b: PersonalAccessToken) =>
  Number(b.get_created_at - a.get_created_at);

interface TokenListProps {
  tokens: PersonalAccessToken[];
  onGenerate: () => void;
  onChanged: () => Promise<unknown>;
  highlightId?: string | null;
}

export const TokenList: React.FC<TokenListProps> = ({
  tokens,
  onGenerate,
  onChanged,
  highlightId,
}) => {
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [pythonOpen, setPythonOpen] = useState(false);
  const [view, setView] = useState<"active" | "inactive">("active");
  const [confirming, setConfirming] = useState<string | null>(null);

  const active = tokens.filter((t) => t.get_status === "active");
  const inactive = tokens.filter((t) => t.get_status !== "active");
  // Removing the last expired or revoked token empties that list: back to active.
  if (view === "inactive" && inactive.length === 0) setView("active");
  const q = query.trim().toLowerCase();
  const list = (view === "inactive" ? inactive : active)
    .filter((t) => !q || t.get_description.toLowerCase().includes(q))
    .sort(newestFirst);

  const switchView = () => {
    setView(view === "inactive" ? "active" : "inactive");
    setShowAll(false);
    setConfirming(null);
  };
  const act = async (
    run: () => Promise<void>,
    done: string,
    failed: string,
  ) => {
    try {
      await run();
      showNotificationGlobal(done, "success");
      await onChanged();
    } catch {
      showNotificationGlobal(failed, "error");
    }
  };
  const revoke = (id: string) => {
    setConfirming(null);
    void act(() => revokeToken(id), "Token revoked", "Failed to revoke token");
  };
  const remove = (id: string) =>
    void act(() => deleteToken(id), "Removed", "Failed to remove token");

  const row = (t: PersonalAccessToken) => (
    <TokenRow
      key={t.get_token_id}
      token={t}
      confirming={confirming === t.get_token_id}
      onConfirming={(open) => setConfirming(open ? t.get_token_id : null)}
      onRevoke={() => revoke(t.get_token_id)}
      onRemove={() => remove(t.get_token_id)}
      highlighted={highlightId === t.get_token_id}
    />
  );

  return (
    <section>
      <SectionRule
        inset
        icon={<VpnKeyOutlinedIcon sx={{ fontSize: 14 }} />}
        label="Personal access tokens"
        trailing={
          active.length > 0 && (
            <span className="whitespace-nowrap">{active.length} active</span>
          )
        }
        end={<MetaAddButton label="New token" onClick={onGenerate} />}
      />
      <div className="flex flex-col gap-3">
        <div>
          {/* eslint-disable-next-line no-restricted-syntax -- a full-width disclosure row, not a Button-styled control */}
          <button
            type="button"
            aria-expanded={pythonOpen}
            className="flex w-full items-center gap-2 item-height px-2 rounded-app cursor-pointer text-fg-medium hover:bg-bg-hover"
            onClick={() => setPythonOpen((v) => !v)}
          >
            <PythonIcon width={16} height={16} aria-hidden />
            Use a token with Python
            <ChevronRightIcon
              className="icon-sm ml-auto text-fg-muted"
              style={{
                rotate: pythonOpen ? "90deg" : "0deg",
                transition: "rotate var(--duration-short) var(--ease-move)",
              }}
            />
          </button>
          <Fold open={pythonOpen}>
            <div className="flex flex-col gap-2 px-2 pt-1 pb-2">
              <p className="text-fg-muted-regular m-0">
                Create a token, then run:
              </p>
              <CodeBlock
                code={PYTHON_QUICKSTART}
                ariaLabel="Shell: install pymycorr and set the token"
                className="bg-bg! border-border select-text"
                onCopy={() => showNotificationGlobal("Copied", "success")}
              />
            </div>
          </Fold>
        </div>

        {tokens.length > MAX_VISIBLE_TOKENS && (
          <div className="w-65 max-w-full">
            <SearchBar
              value={query}
              onChange={setQuery}
              placeholder="Search tokens"
              inputProps={{ "aria-label": "Search tokens" }}
              onKeyDown={(e) => {
                if (e.key === "Escape") setQuery("");
              }}
            />
          </div>
        )}

        {list.length === 0 ? (
          <HintRow>
            {q
              ? `No token matches “${query.trim()}”.`
              : tokens.length
                ? "No active tokens."
                : "No tokens yet. Create one to use MyCorr from a script or an agent."}
          </HintRow>
        ) : (
          <div role="table" aria-label="Personal access tokens">
            <div
              role="row"
              className={`${ROW_GRID} h-7.5 font-mono uppercase tracking-[0.06em] text-fg-muted max-sm:hidden`}
              style={{ fontSize: "var(--font-size-x-small)" }}
            >
              {HEADERS.map((h) => (
                <span key={h} role="columnheader">
                  {h}
                </span>
              ))}
              <span role="columnheader" className="sr-only">
                Actions
              </span>
            </div>
            {list.slice(0, MAX_VISIBLE_TOKENS).map(row)}
            <Fold open={showAll || Boolean(q)}>
              {list.slice(MAX_VISIBLE_TOKENS).map(row)}
            </Fold>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          {!q && list.length > MAX_VISIBLE_TOKENS && (
            <Button
              variant="quiet-link"
              size="xs"
              aria-expanded={showAll}
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? "Show fewer" : `Show all (${list.length})`}
            </Button>
          )}
          {inactive.length > 0 && (
            <Button
              variant="quiet-link"
              size="xs"
              className="ml-auto"
              aria-pressed={view === "inactive"}
              onClick={switchView}
            >
              {view === "inactive"
                ? "Hide expired and revoked"
                : `Show expired and revoked (${inactive.length})`}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};
